import React, { useState, useEffect, useRef, useContext } from "react";
import CancelButton from "../Button/CancelButton";
import SaveButton from "../Button/SaveButton";
import { useTranslation } from "react-i18next";
import { CommonContext } from "../../../App";
import { ImCross } from "react-icons/im";
import api from "../../../services/api";

const FilterModal = ({
  heading,
  tableUniqueId,
  columnUniqueId,
  groupedData,
  isModalOpen,
  setIsModalOpen,
  setAddFilterationValue,
  addFilterationValue,
  selectedGroups,
  setSelectedGroups,
  selectedSubGroups,
  setSelectedSubGroups,
  onUpdate,
  allowedGroupTypes = []
}) => {

  const { branchId } = useContext(CommonContext);
  const { t } = useTranslation();
  const initializedRef = useRef(false);

  const [tempSelectedGroups, setTempSelectedGroups] = useState([]);
  const [tempSelectedSubGroups, setTempSelectedSubGroups] = useState([]);
  const [language, setLanguage] = useState("en");

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("second_language"));
    if (stored?.language) setLanguage(stored.language);
  }, []);

  const visibleGroups = allowedGroupTypes.length
    ? groupedData.filter((g) => allowedGroupTypes.includes(g.type))
    : groupedData;

  const getAllGroupNames = (groups) =>
    groups.map((g) => g.groupName);

  const getAllSubGroupNames = (groups) =>
    groups.flatMap((g) => g.subGroups.map((sg) => sg.subGroupName));

  const addUnique = (arr, value) =>
    arr.includes(value) ? arr : [...arr, value];

  const getSelectedGroupIds = () =>
    visibleGroups
      .filter(g => selectedGroups.includes(g.groupName))
      .map(g => g.id || g.groupId)
      .filter(Boolean);

  const getSelectedSubGroupIds = () =>
    visibleGroups
      .flatMap(g => g.subGroups)
      .filter(sg => selectedSubGroups.includes(sg.subGroupName))
      .map(sg => sg.id || sg.subGroupId)
      .filter(Boolean);

  // -------------------------
  // GET FILTER API
  // -------------------------  

  const getFilterFromBackend = async () => {
    
    if (!branchId || !tableUniqueId || !columnUniqueId) return;
    try {

      const res = await api.get("/column-filter/get-filter-value", {
        params: {
          branchId,
          tableName: tableUniqueId,
          columnName: columnUniqueId
        }
      });

      const data = res?.data?.data;

      // Agar DB me record nahi hai
      if (!data) {

        setSelectedGroups([]);
        setSelectedSubGroups([]);

        setTempSelectedGroups([]);
        setTempSelectedSubGroups([]);

        return;
      }

      const { groupIds = [], subGroupIds = [] } = data;

      const groups = visibleGroups
        .filter(g => groupIds.includes(g.id || g.groupId))
        .map(g => g.groupName);

      const subGroups = visibleGroups
        .flatMap(g => g.subGroups)
        .filter(sg => subGroupIds.includes(sg.id || sg.subGroupId))
        .map(sg => sg.subGroupName);

      setSelectedGroups(groups);
      setSelectedSubGroups(subGroups);

      setTempSelectedGroups(groups);
      setTempSelectedSubGroups(subGroups);

    } catch (error) {

      console.error("Get Filter API Error:", error);

      setSelectedGroups([]);
      setSelectedSubGroups([]);

      setTempSelectedGroups([]);
      setTempSelectedSubGroups([]);

    }
  };

  // -------------------------
  // MODAL OPEN → FETCH DATA
  // -------------------------

  useEffect(() => {

    if (!isModalOpen || !addFilterationValue) {
      initializedRef.current = false;
      return;
    }

    if (!initializedRef.current && visibleGroups.length > 0) {

      getFilterFromBackend();

      initializedRef.current = true;
    }

  }, [isModalOpen, addFilterationValue, visibleGroups]);

  useEffect(() => {
    if (isModalOpen || addFilterationValue) {
      setTempSelectedGroups(selectedGroups);
      setTempSelectedSubGroups(selectedSubGroups);
    }
  }, [selectedGroups, selectedSubGroups]);

  // -------------------------
  // UPDATE FILTER API
  // -------------------------

  const updateFilter = async (groupIds, subGroupIds) => {

    try {

      await api.post("/column-filter/update-filter-value", {
        branchId,
        tableName: tableUniqueId,
        columnName: columnUniqueId,
        groupIds,
        subGroupIds
      });

    } catch (error) {

      console.error("Update Filter API Error:", error);

    }
  };

  // -------------------------
  // SAVE BUTTON
  // -------------------------

  const handleSaveFilter = async () => {

    const groupIds = getSelectedGroupIds();
    const subGroupIds = getSelectedSubGroupIds();

    await updateFilter(groupIds, subGroupIds);

    setIsModalOpen(false);
    setAddFilterationValue(false);

    if (typeof onUpdate === "function") onUpdate();

  };

  const handleCancel = () => {
    setSelectedGroups(tempSelectedGroups);
    setSelectedSubGroups(tempSelectedSubGroups);
    setIsModalOpen(false);
    setAddFilterationValue(false);
  };

  if (!isModalOpen || !addFilterationValue) return null;

  const totalSubGroupCount = visibleGroups.reduce(
    (sum, g) => sum + g.subGroups.length,
    0
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000]">

      <div className="bg-white rounded px-2 pb-3 w-[500px] shadow-lg max-h-[80vh] overflow-y-auto custom-scrollbar">

        <div className="bg-white pt-2 sticky top-0 z-[99]">

          <div className="bg-theme-headerBg text-theme-color flex justify-center rounded-1 py-[2px] px-2 mb-2">

            <div className="ms-auto font-medium">
              {t("selectGroup&SubGroup")}
            </div>

            <div className="ms-auto">
              <ImCross
                className="mt-[5px] text-sm pe-[2px] cursor-pointer"
                onClick={handleCancel}
              />
            </div>

          </div>

        </div>

        {/* SELECT ALL */}

        <label className="flex items-center space-x-2 font-bold mb-3 cursor-pointer text-sm border-b pb-2 px-2">

          <input
            type="checkbox"
            className="accent-theme-headerBg"
            checked={
              selectedGroups.length === visibleGroups.length &&
              selectedSubGroups.length === totalSubGroupCount
            }
            onChange={(e) => {

              if (e.target.checked) {

                setSelectedGroups(getAllGroupNames(visibleGroups));
                setSelectedSubGroups(getAllSubGroupNames(visibleGroups));

              } else {

                setSelectedGroups([]);
                setSelectedSubGroups([]);

              }

            }}
          />

          <span>{t("selectAll")}</span>

        </label>

        {/* GROUPS */}

        <div className="space-y-3 px-2">

          {visibleGroups.map(({ groupName, groupNameInHindi, subGroups }) => (

            <div key={groupName} className="border-b pb-2">

              <label className="flex items-center space-x-2 font-medium cursor-pointer text-sm">

                <input
                  type="checkbox"
                  className="accent-theme-headerBg"
                  checked={selectedGroups.includes(groupName)}
                  onChange={(e) => {

                    if (e.target.checked) {

                      setSelectedGroups(prev => addUnique(prev, groupName));

                      setSelectedSubGroups(prev => [
                        ...prev,
                        ...subGroups
                          .map(sg => sg.subGroupName)
                          .filter(sg => !prev.includes(sg))
                      ]);

                    } else {

                      setSelectedGroups(prev =>
                        prev.filter(g => g !== groupName)
                      );

                      setSelectedSubGroups(prev =>
                        prev.filter(
                          sg => !subGroups.some(s => s.subGroupName === sg)
                        )
                      );

                    }

                  }}
                />

                <span>
                  {groupName} {language !== "en" && groupNameInHindi && `(${groupNameInHindi})`}
                </span>

              </label>

              <div className="pl-6 mt-1 flex gap-3 flex-wrap">

                {subGroups.map(({ subGroupName, subGroupNameInHindi }) => (

                  <label
                    key={subGroupName}
                    className="flex items-center space-x-2 cursor-pointer text-sm"
                  >

                    <input
                      type="checkbox"
                      className="accent-theme-headerBg"
                      checked={selectedSubGroups.includes(subGroupName)}
                      onChange={(e) => {

                        if (e.target.checked) {

                          const newSubs = [...selectedSubGroups, subGroupName];
                          setSelectedSubGroups(newSubs);

                          if (subGroups.every(s => newSubs.includes(s.subGroupName))) {
                            setSelectedGroups(prev => addUnique(prev, groupName));
                          }

                        } else {

                          setSelectedSubGroups(prev =>
                            prev.filter(s => s !== subGroupName)
                          );

                          setSelectedGroups(prev =>
                            prev.filter(g => g !== groupName)
                          );

                        }

                      }}
                    />

                    <span>
                      {subGroupName} {language !== "en" && subGroupNameInHindi && `(${subGroupNameInHindi})`}
                    </span>

                  </label>

                ))}

              </div>

            </div>

          ))}

        </div>

        <div className="flex justify-end space-x-3 mt-4">
          <CancelButton onClick={handleCancel} text={t("cancel")} />
          <SaveButton onClick={handleSaveFilter} text={t("save")} />
        </div>

      </div>

    </div>
  );
};

export default FilterModal;